const APOSTROPHE = "'";

const SIBILANT_ENDING = /(?:ss|ß|tz|s|x|z)$/i;

/**
 * Builds the possessive form of a pet name for the active locale.
 *
 * This cannot live in the message catalogue: a possessive is morphology, not
 * wording, so ICU has no construct for it. It also cannot stay German-only —
 * the value is interpolated into a message, and a German genitive dropped into
 * an English or French sentence reads wrong ("le plan de Bellas").
 *
 * The contract with the catalogue is per language:
 *  - `de` returns the genitive ("Bellos", "Max'"), and the message reads
 *    "{name} neuer Plan".
 *  - `en` returns the English possessive ("Bella's", "Max'"), and the message
 *    reads "{name} new plan".
 *  - everything else returns the name unchanged, because those languages express
 *    possession with a preposition the message supplies itself
 *    ("le nouveau plan de {name}").
 *
 * Pass `intl.locale`. Only the language subtag is inspected, so `de-AT` and
 * `de-CH` behave like `de`.
 *
 * Kept deliberately identical to petcon's src/shared/utils/possessive.ts — both
 * products render the same pet names, so they should inflect them the same way.
 */
export const toPossessive = (name: string | null | undefined, locale: string): string => {
  const trimmed = (name ?? '').trim();

  if (!trimmed) return '';

  const language = locale.slice(0, 2).toLowerCase();

  if (language === 'de') {
    return SIBILANT_ENDING.test(trimmed) ? `${trimmed}${APOSTROPHE}` : `${trimmed}s`;
  }

  if (language === 'en') {
    // English also drops the s after a sibilant ("Max' bowl"), which matches the
    // German rule closely enough to share the test.
    return SIBILANT_ENDING.test(trimmed) ? `${trimmed}${APOSTROPHE}` : `${trimmed}${APOSTROPHE}s`;
  }

  return trimmed;
};
